import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { CardActionArea } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';
import { useNavigate } from 'react-router-dom';

const COLORS = ['#2563eb', '#16a34a', '#7c3aed', '#dc2626', '#ca8a04', '#db2777', '#0d9488'];

const NEVER_USED_LABELS = {
  feeCollection: 'Finance Last Fee Never Used',
  attendance: 'Attendance Never Used',
  homework: 'Homework Never Used',
  exam: 'Exam Never Used',
  examMarks: 'Exam Marks Never Used',
  staffAttendance: 'Staff Attendance Never Used',
  store: 'Store Never Used',
  expenses: 'Expenses Never Used',
};

// Map dashboard card keys to the module schemacolumn used in the report
const NEVER_USED_MODULE_MAP = {
  feeCollection: 'fee_collection_last_used_value',
  attendance: 'student_attendance_last_used_value',
  homework: 'student_diary_last_used_value',
  exam: 'exam_schedule_last_used_value',
  examMarks: 'exam_marks_last_used_value',
  staffAttendance: 'staff_attendance_last_marked_value',
  store: 'item_sold_last_marked_value',
  expenses: 'expenses_last_marked_value',
};

const CLIENT_STATUS_TABS = [
  { value: 'active', label: 'Active' },
  { value: 'dead', label: 'Dead' },
];

const CREATION_TABS = [
  { value: 'created', label: 'Created' },
  { value: 'not_created', label: 'Not Created' },
];

function ClientReportDashboard(props) {
  const navigate = useNavigate();
  const [schoolFilter, setSchoolFilter] = React.useState('');
  const [schoolSearchText, setSchoolSearchText] = React.useState('');
  const [schoolOptions, setSchoolOptions] = React.useState([]);
  const [schoolOptionsLoading, setSchoolOptionsLoading] = React.useState(false);
  const [appliedSchoolFilter, setAppliedSchoolFilter] = React.useState('');
  const [academicYearFilter, setAcademicYearFilter] = React.useState('');
  const [clientStatusTab, setClientStatusTab] = React.useState('active');
  const [creationTab, setCreationTab] = React.useState('created');
  const [dashboardData, setDashboardData] = React.useState({
    neverUsedCounts: {},
    rows: [],
    columns: [],
    tabs: [],
  });
  const [loading, setLoading] = React.useState(false);

  const fetchDashboard = React.useCallback(() => {
    setLoading(true);
    const params = {
      dashboard: 1,
      status_by_team: clientStatusTab,
      creation_status: creationTab,
      ...(appliedSchoolFilter.trim() && { school: appliedSchoolFilter.trim() }),
      ...(academicYearFilter && { tab_name: academicYearFilter }),
    };
    getRequest(
      GET_URL.client_track_report.api,
      params,
      props
    ).then((response) => {
      setLoading(false);
      if (response?.status === 200) {
        setDashboardData(response.data?.data || {});
      }
    }).catch(() => setLoading(false));
  }, [appliedSchoolFilter, academicYearFilter, clientStatusTab, creationTab, props]);

  React.useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const fetchSchoolOptions = React.useCallback((searchText = '') => {
    setSchoolOptionsLoading(true);
    getRequest(
      GET_URL.client_track_report.api,
      {
        institute_options: 1,
        ...(searchText.trim() && { search: searchText.trim() }),
      },
      props
    ).then((response) => {
      setSchoolOptionsLoading(false);
      if (response?.status === 200) {
        setSchoolOptions(response.data?.data || []);
      }
    }).catch(() => setSchoolOptionsLoading(false));
  }, [props]);

  React.useEffect(() => {
    fetchSchoolOptions(schoolSearchText);
  }, [fetchSchoolOptions, schoolSearchText]);

  const neverUsedCounts = dashboardData.neverUsedCounts || {};
  const neverUsedData = Object.entries(NEVER_USED_LABELS).map(([key, label]) => ({
    name: label,
    value: neverUsedCounts[key] || 0,
  })).filter((item) => item.value > 0);
  const pocData = dashboardData.pocSummary || [];
  const availableTabs = dashboardData.tabs || [];
  const statusCounts = dashboardData.statusCounts || {};

  const handleCardClick = (moduleKey) => {
    const schemaColumn = NEVER_USED_MODULE_MAP[moduleKey];
    if (schemaColumn) {
      const params = new URLSearchParams();
      params.set('module', schemaColumn);
      params.set('module_value', 'Never Used');
      if (academicYearFilter) params.set('tab_name', academicYearFilter);
      params.set('status_by_team', clientStatusTab);
      params.set('creation_status', creationTab);
      navigate(`/client-report/report?${params.toString()}`);
    }
  };

  const handleUserCardClick = () => {
    const params = new URLSearchParams();
    if (academicYearFilter)
      params.set('tab_name', academicYearFilter);
      params.set('status_by_team', clientStatusTab);
      params.set('creation_status', creationTab);
      navigate(`/client-report/report?${params.toString()}`);
  };

  return (
    <Box p={2}>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} md={5}>
          <Typography variant="h6" fontWeight={700}>Client Report Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">
            Latest synced usage snapshot {dashboardData.latestTabName ? `for ${dashboardData.latestTabName}` : ''}.
          </Typography>
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            select
            fullWidth
            size="small"
            label="Academic Year"
            value={academicYearFilter}
            onChange={(event) => setAcademicYearFilter(event.target.value)}
          >
            <MenuItem value="">All (Latest)</MenuItem>
            {availableTabs.map((tab) => (
              <MenuItem key={tab} value={tab}>{tab}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <Stack direction="row" spacing={1}>
            <Autocomplete
              freeSolo
              fullWidth
              size="small"
              options={schoolOptions}
              value={schoolFilter || null}
              inputValue={schoolSearchText}
              loading={schoolOptionsLoading}
              onChange={(event, value) => {
                const selectedValue = value || '';
                setSchoolFilter(selectedValue);
                setSchoolSearchText(selectedValue);
                setAppliedSchoolFilter(selectedValue);
              }}
              onInputChange={(event, value, reason) => {
                setSchoolSearchText(value || '');
                if (reason === 'clear') {
                  setSchoolFilter('');
                  setAppliedSchoolFilter('');
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter School"
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      setSchoolFilter(schoolSearchText);
                      setAppliedSchoolFilter(schoolSearchText);
                    }
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {schoolOptionsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={() => {
                setSchoolFilter(schoolSearchText);
                setAppliedSchoolFilter(schoolSearchText);
              }}
            >
              Filter
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ mb: 2, border: '1px solid #e5e7eb', borderRadius: 3, p: 1.5 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ px: 1 }}>
              Client Status
            </Typography>
            <Tabs
              value={clientStatusTab}
              onChange={(event, newValue) => setClientStatusTab(newValue)}
              sx={{ minHeight: 40 }}
            >
              {CLIENT_STATUS_TABS.map((tab) => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  label={(
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{tab.label}</span>
                      {statusCounts[tab.value] !== undefined && (
                        <Chip label={statusCounts[tab.value]} size="small" />
                      )}
                    </Stack>
                  )}
                  sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600 }}
                />
              ))}
            </Tabs>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ px: 1 }}>
              Academic Year Report
            </Typography>
            <Tabs
              value={creationTab}
              onChange={(event, newValue) => setCreationTab(newValue)}
              sx={{ minHeight: 40 }}
            >
              {CREATION_TABS.map((tab) => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  label={(
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{tab.label}</span>
                      {statusCounts[tab.value] !== undefined && (
                        <Chip label={statusCounts[tab.value]} size="small" />
                      )}
                    </Stack>
                  )}
                  sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600 }}
                />
              ))}
            </Tabs>
          </Grid>
        </Grid>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <MetricCard label="Institute" value={dashboardData.totalSchools || 0} />
        </Grid>
        {/* <Grid item xs={12} md={3}>
          <MetricCard 
            label="Users"
            value={dashboardData.loggedInUsers || 0}
            color='#f59e0b'
            onClick={handleUserCardClick}
          />
        </Grid> */}
        <Grid item xs={12} md={3}>
          <MetricCard
            label="Total Users"
            value={dashboardData.totalClientUsers || 0}
            // activeCount={dashboardData.activeUsers || 0}
            // inactiveCount={dashboardData.inactiveUsers || 0}
            color='#4680FF'
            onClick={handleUserCardClick}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard 
            label="Staff Users"
            value={dashboardData.staffUsers || 0}
            // activeCount={dashboardData.activeStaffClientUsers || 0}
            color="#e11d48"
            onClick={handleUserCardClick}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard
            label="Student Users"
            value={dashboardData.studentUsers || 0}
            // activeCount={dashboardData.activeStudentClientUsers || 0}
            color="#16a34a"
            onClick={handleUserCardClick}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard
            label="Finance Last Fee Never Used"
            value={neverUsedCounts.feeCollection || 0}
            color="#0d9488"
            onClick={() => handleCardClick('feeCollection')}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard
            label="Attendance Never Used"
            value={neverUsedCounts.attendance || 0}
            color="#2563eb"
            onClick={() => handleCardClick('attendance')}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard
            label="Homework Never Used"
            value={neverUsedCounts.homework || 0}
            color="#16a34a"
            onClick={() => handleCardClick('homework')}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          ['exam', 'Exam Never Used', '#7c3aed'],
          ['examMarks', 'Exam Marks Never Used', '#db2777'],
          ['staffAttendance', 'Staff Attendance Never Used', '#dc2626'],
          ['store', 'Store Never Used', '#ca8a04'],
          ['expenses', 'Expenses Never Used', '#db2777'],
        ].map(([key, label, color]) => (
          <Grid item xs={12} md={4} lg={2.4} key={key}>
            <MetricCard
              label={label}
              value={neverUsedCounts[key] || 0}
              color={color}
              onClick={() => handleCardClick(key)}
            />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <ChartCard title="Never Used Modules">
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={neverUsedData.length ? neverUsedData : [{ name: 'No data', value: 1 }]}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={4}
                >
                  {(neverUsedData.length ? neverUsedData : [{ name: 'No data', value: 1 }]).map((entry, index) => (
                    <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={7}>
          <ChartCard title="Institute by coordinator">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={pocData} layout="vertical" margin={{ top: 10, right: 30, left: 60, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} />
                <YAxis dataKey="name" type="category" width={140} />
                <Tooltip />
                <Bar dataKey="value" name="Institute" fill="#4680FF" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
}

const MetricCard = ({ label, value, color = '#4680FF', onClick, activeCount, inactiveCount }) => (
  <Card
    elevation={0}
    sx={{
      border: '1px solid #e5e7eb',
      borderRadius: 3,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
      ...(onClick ? {
        '&:hover': {
          borderColor: color,
          boxShadow: `0 6px 20px ${color}33`,
          transform: 'translateY(-2px)',
        },
      } : {}),
    }}
    onClick={onClick}
  >
    <CardContent>
      <Typography variant="overline" color="text.secondary" fontWeight={700}>
        {label}
      </Typography>
      <Typography variant="h4" fontWeight={800} color={color}>
        {value}
      </Typography>
      {(activeCount !== undefined || inactiveCount !== undefined) && (
        <Stack direction="row" spacing={2} sx={{ mt:1}}>
          {activeCount !== undefined && (
            <Typography variant="caption" color="success.main" fontWeight={700}>
              Active:{activeCount}
              {/* {value >0 ? `(${((activeCount / value) * 100).toFixed(1)}%)` : ''} */}
            </Typography>
          )}
          {inactiveCount !== undefined && (
            <Typography variant="caption" color="error.main" fontWeight={700}>
              Inactive:{inactiveCount}
              {/* {value > 0 ? `(${((inactiveCount / value) * 100).toFixed(1)}%)` : '' } */}
            </Typography>
          )} 
        </Stack>
      )}
      {onClick && (
        <Typography variant="caption" color="primary" sx={{ mt: 0.5 }}>
          Click to view report →
        </Typography>
      )}
    </CardContent>
  </Card>
);

const ChartCard = ({ title, children }) => (
  <Card elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 3, height: '100%' }}>
    <CardContent>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
        {title}
      </Typography>
      {children}
    </CardContent>
  </Card>
);

export default ClientReportDashboard;
